import { useEffect, useState, type ReactNode } from 'react';
import { skillsApi, type SkillShopEntry, type SkillType } from '@/skills/skillsApi';
import { useSkillsStore } from '@/stores/skillsStore';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { SkillIcon } from '@/art';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import { adsAvailable, showRewarded } from '@/ads/adService';
import { SaleBadge, SalePrice } from '@/shop/sale';

const SKILL_META: Record<SkillType, { name: string; color: string; icon: (s: number) => JSX.Element; descKey: I18nKey }> = {
  disrupt: {
    name: 'DISRUPT',
    color: 'var(--c-purple)',
    icon: (s) => <SkillIcon skill="disrupt" size={s} />,
    descKey: 'shop.disrupt.desc',
  },
  shield: {
    name: 'SHIELD',
    color: 'var(--c-cyan)',
    icon: (s) => <SkillIcon skill="shield" size={s} />,
    descKey: 'shop.shield.desc',
  },
};

export function SkillsShopSection() {
  const t = useT();
  const setProfile = useUserStore((s) => s.setProfile);
  const token = useAuthStore((s) => s.token);
  const refreshSkills = useSkillsStore((s) => s.refresh);
  const items = useSkillsStore((s) => s.items);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => { void refreshSkills(); }, [refreshSkills]);

  function flash(kind: 'ok' | 'err', text: string) {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function refreshProfile() {
    if (!token) return;
    try {
      const { user } = await authApi.me(token);
      setProfile({
        id: user.id,
        name: user.displayName,
        avatarColor: user.avatarColor,
        avatarUrl: user.avatarUrl,
        mmr: user.mmr,
        rank: user.rank,
        rankDivision: user.rankDivision,
        rankStars: user.rankStars,
        coins: user.coins,
        gems: user.gems,
        xp: user.xp,
        winStreak: user.winStreak,
        matchesPlayed: user.matchesPlayed,
        matchesWon: user.matchesWon,
      });
    } catch { /* */ }
  }

  async function buy(skill: SkillType, currency: 'coins' | 'gems') {
    setBusy(`${skill}-${currency}`);
    try {
      await skillsApi.buy(skill, currency, 1);
      audio.play('ui.shop_purchase');
      await Promise.all([refreshSkills(), refreshProfile()]);
      flash('ok', t('shop.skills.added', { name: skill.toUpperCase() }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('shop.skills.purchase_failed'));
    }
    setBusy(null);
  }

  async function watchAd(skill: SkillType) {
    setBusy(`${skill}-ad`);
    // Show a real AdMob rewarded video; only grant the charge (server enforces
    // the cap) when the user actually finished the ad.
    try {
      const earned = await showRewarded();
      if (!earned) { setBusy(null); return; }
      await skillsApi.adReward(skill);
      audio.play('ui.shop_purchase');
      await refreshSkills();
      flash('ok', t('shop.skills.added_ad', { name: skill.toUpperCase() }));
    } catch (e: any) {
      audio.play('ui.error');
      flash('err', e?.message ?? t('shop.skills.ad_failed'));
    }
    setBusy(null);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tag" style={{ padding: '8px 2px 8px', color: 'var(--c-cyan)' }}>
        {t('shop.skills.section')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((it) => (
          <SkillRow
            key={it.skill}
            item={it}
            busy={busy}
            onBuy={(currency) => buy(it.skill, currency)}
            onAd={() => watchAd(it.skill)}
          />
        ))}
        {items.length === 0 && (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('shop.skills.loading')}
          </div>
        )}
      </div>
      {toast && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'center',
            background: toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

interface SkillRowProps {
  item: SkillShopEntry;
  busy: string | null;
  onBuy: (currency: 'coins' | 'gems') => void;
  onAd: () => void;
}

function SkillRow({ item, busy, onBuy, onAd }: SkillRowProps) {
  const t = useT();
  const meta = SKILL_META[item.skill];
  // Daily limit applies to GOLD buys only — gem buys are unlimited.
  const remainingPaid = Math.max(0, item.dailyLimit - item.boughtToday);
  // dailyAdLimit <= 0 means unlimited rewarded-ad charges.
  const adUnlimited = item.dailyAdLimit <= 0;
  const adAtLimit = !adUnlimited && item.adToday >= item.dailyAdLimit;
  const adBusy = busy === `${item.skill}-ad`;
  const coinBusy = busy === `${item.skill}-coins`;
  const gemBusy = busy === `${item.skill}-gems`;

  return (
    <div
      className="card"
      style={{
        padding: 12,
        borderColor: item.unlocked ? `${meta.color}40` : 'var(--hairline)',
        opacity: item.unlocked ? 1 : 0.55,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <div
          style={{
            width: 64,
            height: 64,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `${meta.color}1f`,
            border: `1px solid ${meta.color}55`,
            borderRadius: 12,
          }}
        >
          {meta.icon(44)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="display" style={{ fontSize: 13, color: meta.color, letterSpacing: '0.1em' }}>
            {meta.name}
          </div>
          <div style={{ marginTop: 4 }}><SaleBadge sale={item.sale} /></div>
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 2 }}>{t(meta.descKey)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: 'var(--ink-dim)', fontFamily: 'var(--f-display)' }}>{t('shop.skills.owned')}</div>
          <div className="mono" style={{ fontSize: 16, color: 'var(--c-cyan)' }}>×{item.owned}</div>
        </div>
      </div>

      {!item.unlocked && (
        <div
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            background: 'rgba(230,57,70,0.12)',
            color: 'var(--c-red)',
            fontSize: 10,
            textAlign: 'center',
            marginBottom: 8,
          }}
        >
          {t('shop.skills.unlocks_at', { level: item.unlockLevel, my: item.myLevel })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {item.priceCoins > 0 && (
          <BuyButton
            color="var(--c-yellow)"
            disabled={!item.unlocked || remainingPaid <= 0 || coinBusy}
            onClick={() => onBuy('coins')}
            label={coinBusy ? '…' : (
              <SalePrice
                price={item.priceCoins}
                original={item.originalPriceCoins}
                currency="coins"
              />
            )}
          />
        )}
        {item.priceGems > 0 && (
          <BuyButton
            color="var(--c-purple)"
            disabled={!item.unlocked || gemBusy}
            onClick={() => onBuy('gems')}
            label={gemBusy ? '…' : (
              <SalePrice
                price={item.priceGems}
                original={item.originalPriceGems}
                currency="gems"
              />
            )}
          />
        )}
        {adsAvailable() && (
          <BuyButton
            color="var(--c-cyan)"
            disabled={!item.unlocked || adAtLimit || adBusy}
            onClick={onAd}
            label={adBusy ? t('shop.skills.watching') : `📺 AD`}
            dashed
          />
        )}
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 9,
          color: 'var(--ink-dim)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{t('shop.skills.bought_today', { n: item.boughtToday, max: item.dailyLimit })}</span>
        <span>{t('shop.skills.ad_today', { n: item.adToday, max: adUnlimited ? '∞' : item.dailyAdLimit })}</span>
      </div>
    </div>
  );
}

function BuyButton({
  color, disabled, onClick, label, dashed,
}: {
  color: string;
  disabled: boolean;
  onClick: () => void;
  label: ReactNode;
  dashed?: boolean;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: 1,
        minWidth: 80,
        padding: '8px 10px',
        border: `1px ${dashed ? 'dashed' : 'solid'} ${color}`,
        background: disabled ? 'rgba(0,0,0,0.3)' : `${color}1f`,
        color: disabled ? 'var(--ink-dim)' : color,
        fontFamily: 'var(--f-display)',
        fontSize: 11,
        letterSpacing: '0.06em',
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}
